// ---------------------------------------------------------------------------
// NarrationPicker — controls the AI scenic narration: how much of it you hear,
// or whether you hear it at all.
//
// Real-drive note: "you should be able to turn off narration as well — would
// get annoying on a drive you do all the time." So "Off" is a first-class
// choice here, not buried.
//
// The picker also surfaces a one-line diagnostic of the most recent narration
// attempt — so a silent drive can be told apart from a broken one without a
// developer console.
// ---------------------------------------------------------------------------

import { useState } from "react";
import { Check } from "lucide-react";
import {
  getNarrationIntensity,
  setNarrationIntensity,
  getLastNarrationOutcome,
  type NarrationIntensity,
} from "@/utils/narrationSettings";

const OPTIONS: { id: NarrationIntensity; label: string; blurb: string }[] = [
  { id: "off",      label: "Off",      blurb: "No spoken narration — silence on a drive you know well." },
  { id: "quiet",    label: "Quiet",    blurb: "At most one narration the whole drive." },
  { id: "balanced", label: "Balanced", blurb: "An occasional note — every few minutes at most." },
  { id: "generous", label: "Generous", blurb: "More to listen to — the talkative setting." },
];

function rowClass(selected: boolean): string {
  return `w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition-smooth ${
    selected
      ? "border-primary/60 bg-pleasant/10"
      : "border-border bg-card hover:bg-muted/50"
  }`;
}

/** Turn a stored outcome into a plain, friendly status line. */
function diagnosticLine(): string {
  const o = getLastNarrationOutcome();
  if (!o) return "No narration activity recorded yet — take a drive to see how it's doing.";
  const when = new Date(o.at).toLocaleDateString([], { month: "short", day: "numeric" });
  const feature = o.feature ? ` (${o.feature})` : "";
  switch (o.reason) {
    case "spoke":
      return `Last narration${feature} played on ${when} — working.`;
    case "edge-failed":
      return `On ${when} the narration service couldn't be reached${feature}. If this keeps happening, the AI key may be missing on the server.`;
    case "no-text":
      return `On ${when} the narration service answered but had nothing to say${feature}.`;
    case "off":
      return "Narration is currently off.";
    case "gated:cap":
      return `The free monthly narration limit was reached on ${when}.`;
    default:
      return `Last narration check on ${when}: ${o.reason}.`;
  }
}

export const NarrationPicker = () => {
  const [chosen, setChosen] = useState<NarrationIntensity>(() => getNarrationIntensity());
  // Snapshot the diagnostic once on mount — it reflects the last drive.
  const [diagnostic] = useState<string>(() => diagnosticLine());

  const pick = (id: NarrationIntensity) => {
    setNarrationIntensity(id);
    setChosen(id);
  };

  return (
    <div className="space-y-1.5">
      {OPTIONS.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => pick(o.id)}
          className={rowClass(chosen === o.id)}
        >
          <span className="flex flex-col text-left min-w-0">
            <span className="text-sm font-medium">{o.label}</span>
            <span className="text-[11px] text-muted-foreground">{o.blurb}</span>
          </span>
          {chosen === o.id && <Check className="w-4 h-4 text-primary shrink-0" />}
        </button>
      ))}

      <p className="text-[11px] text-muted-foreground/80 pt-1.5 leading-snug">
        {diagnostic}
      </p>
    </div>
  );
};
